/**
 * Service worker registration with an update lifecycle hook.
 *
 * Why this shape:
 *   - Don't register in dev — Vite serves modules with HMR; a stale SW would
 *     cache them and confuse the build → use import.meta.env.PROD as the gate.
 *   - When the SW finds a new version, it goes into "waiting" state until the
 *     page reloads. We surface this via a callback so the UI can prompt the
 *     user to refresh — cheaper and more transparent than auto-reload.
 */

export interface SWRegistrationCallbacks {
  /** Called when a new SW is installed and waiting to take over. */
  onUpdateReady?: (registration: ServiceWorkerRegistration) => void
  /** Called once the SW is registered and active. */
  onReady?: (registration: ServiceWorkerRegistration) => void
  /** Called if registration fails (e.g. unsupported browser). */
  onError?: (error: unknown) => void
}

export function registerServiceWorker(callbacks: SWRegistrationCallbacks = {}): void {
  if (typeof window === 'undefined') return
  if (!('serviceWorker' in navigator)) return
  if (!import.meta.env.PROD) return

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        callbacks.onReady?.(registration)

        // If a new SW is already waiting (user opened a new tab), surface immediately
        if (registration.waiting) {
          callbacks.onUpdateReady?.(registration)
        }

        // Watch for new SW installations
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          if (!newWorker) return
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Existing SW is in control AND new SW is installed → update available
              callbacks.onUpdateReady?.(registration)
            }
          })
        })
      })
      .catch((err) => callbacks.onError?.(err))

    // Reload once the new SW takes over so the user sees fresh code
    let reloading = false
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return
      reloading = true
      window.location.reload()
    })
  })
}

export function activateWaitingWorker(registration: ServiceWorkerRegistration): void {
  registration.waiting?.postMessage('SKIP_WAITING')
}
